import aliasFile from '../data/dimension-aliases.json'
import { lastSegment, readReference } from './normalise'
import type { DimensionRef } from './types'

/**
 * Reading dimension references.
 *
 * A reference may be written as a bare string, as an object carrying an @id, or
 * as a list of either, and an indicator commonly declares several. Slugs are
 * also occasionally misspelt upstream, which would otherwise strand an
 * indicator outside every dimension.
 *
 * So: take the last path segment, trim it, and run it through an alias table
 * held as data. This mirrors the normalisation the previous dashboard had to
 * apply in the database, with one deliberate difference. That version read only
 * the first entry of a list, silently discarding the rest; a third of all
 * indicators declare more than one dimension, so this one keeps them all.
 */

const ALIASES: Record<string, string> = (aliasFile as { aliases: Record<string, string> }).aliases

const DIMENSION_BASE = 'https://w3id.org/everse/i/dimensions/'

/** Used when an indicator declares no dimension, or none that could be read. */
export const UNCLASSIFIED: DimensionRef = {
  uri: '',
  slug: 'unclassified',
  name: 'Unclassified',
  description: 'The indicator does not state which quality dimension it belongs to.',
}

/**
 * Reads one reference. Returns undefined when nothing usable is there, so
 * callers can tell "absent" from "present but unrecognised".
 */
export function readDimensionRef(value: unknown): DimensionRef | undefined {
  const raw = readReference(value) ?? (typeof value === 'string' ? value : undefined)
  if (!raw) return undefined

  const written = lastSegment(raw).toLowerCase()
  if (!written) return undefined

  const corrected = ALIASES[written]
  const slug = corrected ?? written

  return {
    uri: raw.includes('/') ? raw : `${DIMENSION_BASE}${slug}`,
    slug,
    name: humanise(slug),
    aliased: corrected !== undefined,
  }
}

/**
 * Reads however many dimensions an indicator declares.
 *
 * An indicator belonging to several dimensions counts in each of them. That
 * makes dimension groupings overlap: per-dimension totals will not add up to
 * the number of indicators, which is a property of the vocabulary rather than
 * an error, and the interface has to say so.
 */
export function readDimensionRefs(value: unknown): DimensionRef[] {
  const items = Array.isArray(value) ? value : [value]
  const seen = new Map<string, DimensionRef>()
  for (const item of items) {
    const ref = readDimensionRef(item)
    if (ref && !seen.has(ref.slug)) seen.set(ref.slug, ref)
  }
  return [...seen.values()]
}

/** Turns a slug into something readable, for when the registry has no entry. */
export function humanise(slug: string): string {
  if (!slug) return 'Unknown'
  const words = slug.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim()
  return tidyName(words.charAt(0).toUpperCase() + words.slice(1))
}

/** Written in capitals wherever they appear in a name, whatever the source wrote. */
const ACRONYMS = ['CI/CD', 'CI', 'CD', 'API', 'URL', 'DOI', 'SBOM', 'FAIR', 'README', 'JSON', 'CFF']

/**
 * A name as it should read: acronyms in capitals ("ci/cd" is "CI/CD") and no
 * full stop at the end, which some registry names carry.
 */
export function tidyName(name: string): string {
  let tidy = name.trim().replace(/\.+$/, '')
  for (const acronym of ACRONYMS) {
    tidy = tidy.replace(new RegExp(`\\b${acronym.replace('/', '\\/')}\\b`, 'gi'), acronym)
  }
  return tidy
}
